import {
    MedicalActRecord,
    CreateMedicalActRecordDto,
    QueryMedicalActRecordDto,
    MedicalActStatisticsSummary,
    MedicalActStatisticsByPerformer,
    MedicalActDetailedRecordsResponse,
} from '@/core/models/medical-act.model'

import { cacher, cacheByIdArg } from '@/utils/rtkQueryCacheUtils'
import { baseApi, Tag } from './base'
import { SearchRequestOptions } from '@/core/types/request'

type GetStatisticsParams = {
    startDate?: string
    endDate?: string
    establishment?: string
}

type GetDetailedRecordsParams = GetStatisticsParams & SearchRequestOptions

export const medicalActRecordApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Get all records for the user's establishment with optional filters
         */
        getMedicalActRecords: builder.query<MedicalActRecord[], QueryMedicalActRecordDto | void>({
            query: (params) => ({
                url: '/medical-act-records',
                method: 'GET',
                params: params || {},
            }),
            providesTags: cacher.providesList(Tag.MEDICAL_ACT_RECORD),
        }),

        /**
         * Get a single record by ID
         */
        getMedicalActRecord: builder.query<MedicalActRecord, string>({
            query: (id) => `/medical-act-records/${id}`,
            providesTags: cacheByIdArg(Tag.MEDICAL_ACT_RECORD),
        }),

        /**
         * Get records by consultation
         */
        getMedicalActRecordsByConsultation: builder.query<MedicalActRecord[], string>({
            query: (consultationId) => `/medical-act-records/consultation/${consultationId}`,
            providesTags: (result, error, consultationId) => [
                { type: Tag.MEDICAL_ACT_RECORD, id: `consultation-${consultationId}` },
                { type: Tag.MEDICAL_ACT_RECORD, id: 'LIST' },
            ],
        }),

        /**
         * Get records by expertise
         */
        getMedicalActRecordsByExpertise: builder.query<MedicalActRecord[], string>({
            query: (expertiseId) => `/medical-act-records/expertise/${expertiseId}`,
            providesTags: (result, error, expertiseId) => [
                { type: Tag.MEDICAL_ACT_RECORD, id: `expertise-${expertiseId}` },
                { type: Tag.MEDICAL_ACT_RECORD, id: 'LIST' },
            ],
        }),

        /**
         * Get records by patient
         */
        getMedicalActRecordsByPatient: builder.query<MedicalActRecord[], string>({
            query: (patientId) => `/medical-act-records/patient/${patientId}`,
            providesTags: (result, error, patientId) => [
                { type: Tag.MEDICAL_ACT_RECORD, id: `patient-${patientId}` },
                { type: Tag.MEDICAL_ACT_RECORD, id: 'LIST' },
            ],
        }),

        /**
         * Get statistics summary for the establishment (grouped by act type)
         */
        getMedicalActStatistics: builder.query<MedicalActStatisticsSummary, GetStatisticsParams | void>({
            query: (params) => ({
                url: '/medical-act-records/statistics/summary',
                method: 'GET',
                params: params || {},
            }),
            providesTags: [{ type: Tag.MEDICAL_ACT_RECORD, id: 'STATISTICS' }],
        }),

        /**
         * Get statistics grouped by performer (who performed the acts)
         */
        getMedicalActStatisticsByPerformer: builder.query<MedicalActStatisticsByPerformer, GetStatisticsParams | void>({
            query: (params) => ({
                url: '/medical-act-records/statistics/by-performer',
                method: 'GET',
                params: params || {},
            }),
            providesTags: [{ type: Tag.MEDICAL_ACT_RECORD, id: 'STATISTICS_BY_PERFORMER' }],
        }),

        /**
         * Get detailed records for "who quoted what when" view
         */
        getMedicalActDetailedRecords: builder.query<MedicalActDetailedRecordsResponse, GetDetailedRecordsParams | void>({
            query: (params) => ({
                url: '/medical-act-records/statistics/detailed',
                method: 'GET',
                params: params || {},
            }),
            providesTags: [{ type: Tag.MEDICAL_ACT_RECORD, id: 'DETAILED' }],
        }),

        /**
         * Create a new record
         */
        createMedicalActRecord: builder.mutation<MedicalActRecord, CreateMedicalActRecordDto>({
            query: (body) => ({
                url: '/medical-act-records',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, body) => [
                { type: Tag.MEDICAL_ACT_RECORD, id: 'LIST' },
                { type: Tag.MEDICAL_ACT_RECORD, id: 'STATISTICS' },
                // Also invalidate context-specific caches
                ...(body.consultation ? [{ type: Tag.MEDICAL_ACT_RECORD as const, id: `consultation-${body.consultation}` }] : []),
                ...(body.expertise ? [{ type: Tag.MEDICAL_ACT_RECORD as const, id: `expertise-${body.expertise}` }] : []),
                ...(body.patient ? [{ type: Tag.MEDICAL_ACT_RECORD as const, id: `patient-${body.patient}` }] : []),
            ],
        }),

        /**
         * Delete a record
         */
        deleteMedicalActRecord: builder.mutation<MedicalActRecord, string>({
            query: (id) => ({
                url: `/medical-act-records/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                { type: Tag.MEDICAL_ACT_RECORD, id: 'LIST' },
                { type: Tag.MEDICAL_ACT_RECORD, id: 'STATISTICS' },
            ],
        }),
    }),
    overrideExisting: false,
})
